import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useDebounce } from '../../../common/Debounce.hooks';
import { useFindWorkflows } from '../Workflow.hooks';
import { WorkflowDto } from '../Workflow.interface';

export interface WorkflowSelectorProps {
  value?: number;
  disabled?: boolean;
  className?: string;
  relationTarget?: string;
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onChange?: (value: WorkflowDto | undefined) => void;
}

const WorkflowSelector: React.FC<WorkflowSelectorProps> = (
  props: WorkflowSelectorProps
) => {
  const {
    value: workflowId,
    disabled,
    className,
    relationTarget,
    textFieldProps,
    onChange
  } = props;
  const onChangeRef = useRef(onChange);
  const didAutoSelectRef = useRef(false);
  const previousWorkflowId = useRef(workflowId);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<WorkflowDto>();
  const [workflows, setWorkflows] = useState<WorkflowDto[]>([]);
  const debouncedSearch = useDebounce(search, 300);
  const { workflowList, isLoading, loadingError, findWorkflows } =
    useFindWorkflows();

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    findWorkflows({ search: debouncedSearch || '' });
  }, [debouncedSearch, findWorkflows]);

  useEffect(() => {
    workflowList?.data && setWorkflows(workflowList.data);
  }, [workflowList]);

  // if workflowId changes, reselect workflow
  useEffect(() => {
    if (previousWorkflowId.current !== workflowId) {
      setSelected(undefined);
      previousWorkflowId.current = workflowId;
    }
  }, [workflowId]);

  // auto select workflow based on relationTarget
  useEffect(() => {
    if (!relationTarget) {
      didAutoSelectRef.current = false;
      return;
    }
    if (didAutoSelectRef.current) {
      return;
    }
    const workflow = workflows.find(
      workflow => workflow.taskType?.targetEntity === relationTarget
    );
    if (workflow) {
      didAutoSelectRef.current = true;
      onChangeRef.current?.(workflow);
    }
  }, [workflows, relationTarget]);

  useEffect(() => {
    // if no selected workflow yet
    if (selected) {
      return;
    }
    const selectedWorkflow: WorkflowDto | undefined = workflows.find(
      (workflowDto: WorkflowDto) => workflowDto.id === workflowId
    );
    if (selectedWorkflow) {
      setSelected(selectedWorkflow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflows, workflowId]);

  return (
    <Autocomplete
      value={selected || null}
      options={workflows}
      disabled={disabled}
      loading={isLoading}
      className={className}
      getOptionLabel={option => option.name}
      getOptionSelected={(option, value) => option.id === value?.id}
      onInputChange={(_, value) => setSearch(value || '')}
      onChange={(_, value) => onChange?.(value || undefined)}
      renderOption={option => {
        const secondary = option.taskTypeName || option.entity;
        return (
          <span>
            <Typography>{option.name}</Typography>
            {secondary && (
              <Typography variant='body2' color='textSecondary'>
                {secondary}
              </Typography>
            )}
          </span>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Workflow'
          error={!!loadingError}
          helperText={loadingError?.message}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default WorkflowSelector;
